import { Plus, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import type { Column, FilterOption } from "@/components/shared/data-table";
import { DataTable } from "@/components/shared/data-table";
import { PageHeader } from "@/components/shared/page-header";
import { StatusToggleBadge } from "@/components/shared/status-toggle-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useHasPermission, useMe } from "@/hooks/use-auth";
import { useServerTable } from "@/hooks/use-server-table";
import { useDeleteUom, useUpdateUom } from "@/hooks/use-uoms";
import { useWorkspaceFilterOption } from "@/hooks/use-workspace-filter";
import { useWorkspaces } from "@/hooks/use-workspaces";
import { getErrorMessage } from "@/lib/error-message";
import { queryKeys } from "@/lib/query-keys";
import { uomService } from "@/services/uom";

import { UomDialog } from "./uom-dialog";

import type {
  UnitOfMeasurement,
  UnitOfMeasurementCategory,
} from "@artisancode/api-types";

// ponytail: hardcoded category set (not master data) — see
// UNIT_OF_MEASUREMENT_CATEGORIES in @artisancode/api-types, the source of
// truth both this Select and any future BE schema enum validate against.
export const categoryLabel: Record<UnitOfMeasurementCategory, string> = {
  length: "Panjang",
  area: "Luas",
  volume: "Volume",
  mass: "Massa",
  time: "Waktu",
  quantity: "Jumlah",
  other: "Lainnya",
};

export const categoryOptions = Object.entries(categoryLabel).map(
  ([value, label]) => ({
    value,
    label,
  }),
);

const categoryFilters: FilterOption[] = [
  { key: "category", label: "Kategori", options: categoryOptions },
];

export function Uoms() {
  const canCreate = useHasPermission("uoms.create");
  const canUpdate = useHasPermission("uoms.update");
  const canDelete = useHasPermission("uoms.delete");

  const { mutate: update } = useUpdateUom();
  const { mutate: deleteUom, isPending: isDeleting } = useDeleteUom();
  const { data: me } = useMe();
  const { data: workspacesData } = useWorkspaces({ per_page: 100 });
  const workspaces = workspacesData?.items ?? [];

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<UnitOfMeasurement | null>(null);
  const [deleting, setDeleting] = useState<UnitOfMeasurement | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const workspaceFilter = useWorkspaceFilterOption();
  const table = useServerTable<UnitOfMeasurement>({
    queryKey: (params) => queryKeys.uoms.list(params),
    fetcher: (params) => uomService.list(params),
    pageSize: 10,
    filterKeys: ["workspace_id", ...categoryFilters.map((f) => f.key)],
  });

  function openAdd() {
    setEditing(null);
    setOpen(true);
  }

  function openEdit(item: UnitOfMeasurement) {
    setEditing(item);
    setOpen(true);
  }

  function toggleActive(item: UnitOfMeasurement) {
    if (!canUpdate || updatingId === item.id) return;
    setUpdatingId(item.id);
    update(
      { id: item.id, isActive: !item.isActive },
      {
        onSuccess: () =>
          toast.success(item.isActive ? "Dinonaktifkan." : "Diaktifkan."),
        onError: () => toast.error("Gagal mengubah status satuan."),
        onSettled: () => setUpdatingId(null),
      },
    );
  }

  function workspaceName(workspaceId: string) {
    return workspaces.find((w) => w.id === workspaceId)?.name ?? "-";
  }

  const columns: Column<UnitOfMeasurement>[] = [
    {
      key: "name",
      label: "Nama Satuan",
      render: (i) => <span className="font-medium">{i.name}</span>,
    },
    {
      key: "symbol",
      label: "Simbol",
      render: (i) => i.symbol,
    },
    {
      key: "category",
      label: "Kategori",
      render: (i) => (
        <Badge variant="outline">{categoryLabel[i.category]}</Badge>
      ),
    },
    ...(me?.canSwitchWorkspace
      ? [
          {
            key: "workspace",
            label: "Workspace",
            render: (i: UnitOfMeasurement) => (
              <span className="text-sm">{workspaceName(i.workspaceId)}</span>
            ),
          },
        ]
      : []),
    {
      key: "status",
      label: "Status",
      render: (i) => (
        <StatusToggleBadge
          isActive={i.isActive}
          isLoading={updatingId === i.id}
          disabled={!canUpdate}
          onClick={() => toggleActive(i)}
        />
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Satuan"
        action={
          <Button size="sm" onClick={openAdd} disabled={!canCreate}>
            <Plus className="mr-1 h-4 w-4" />
            Tambah
          </Button>
        }
      />
      <DataTable
        data={table.items}
        loadedData={table.loadedItems}
        columns={columns}
        loading={table.loading}
        searchPlaceholder="Cari satuan..."
        query={table.query}
        onQueryChange={table.onQueryChange}
        filters={[...workspaceFilter, ...categoryFilters]}
        activeFilters={table.activeFilters}
        onFilterChange={table.onFilterChange}
        page={table.page}
        totalPages={table.totalPages}
        totalCount={table.totalCount}
        onPageChange={table.onPageChange}
        hasMore={table.hasMore}
        onLoadMore={table.onLoadMore}
        actions={(item) => (
          <div className="flex items-center gap-1">
            {canUpdate && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => openEdit(item)}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            )}
            {canDelete && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setDeleting(item)}
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      />

      <UomDialog open={open} onOpenChange={setOpen} editing={editing} />

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(isOpen) => !isOpen && setDeleting(null)}
        title="Hapus Satuan"
        description={`Apakah Anda yakin ingin menghapus satuan "${deleting?.name}" (${deleting?.symbol})? Tindakan ini tidak dapat dibatalkan.`}
        confirmLabel="Hapus"
        isConfirming={isDeleting}
        onConfirm={() => {
          if (!deleting) return;
          deleteUom(deleting.id, {
            onSuccess: () => {
              toast.success("Satuan berhasil dihapus.");
              setDeleting(null);
            },
            onError: (err: Error) => {
              toast.error(getErrorMessage(err, "Gagal menghapus satuan."));
            },
          });
        }}
      />
    </div>
  );
}
